import React, { useEffect, useState, useMemo, useRef } from 'react';
import { DateTimeDisplay, ActionList, Breadcrumbs } from '../../components/ui';
import StatusMessage, { StatusKind } from "../../components/ui/StatusMessage";
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { apiService, deleteAccessRequest } from '../../services';
import { listUsers, getRoles, updateUserRole } from '../../services/userManagement/userManagement.service';
import type { UserListItem, UserRole } from '../../services/userManagement/userManagement.types';
import { formatDateTime } from '../../helpers/formatDateTime';
import Modal from '../../components/ui/Modal';
import { ModalContent } from '../../components/ui/ModalContent';
import SortIcon from '../../components/ui/Sorting';
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs';

type SortColumn = 'name' | 'email' | 'description' | 'created';
type SortOrder = 'asc' | 'desc';
type AccessRequestItem = {
  id?: string | number;
  firstName?: string;
  lastName?: string;
  userFirstName?: string;
  userLastName?: string;
  name?: string;
  email?: string;
  userEmail?: string;
  contactEmail?: string;
  requestedAt?: string;
  createdAt?: string;
  reason?: string;
  description?: string;
  requestReason?: string;
  status?: string;
  employeeId?: string;
  employeeID?: string;
  user?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  [key: string]: unknown;
};

const getDisplayName = (item: AccessRequestItem): string => {
  const directFirstName = item.firstName ?? item.userFirstName ?? item.user?.firstName;
  const first = directFirstName ?? item.name ?? '';
  const last = item.lastName ?? item.userLastName ?? item.user?.lastName ?? '';
  const fullName = `${first} ${last}`.trim();
  if (fullName) {
    return fullName;
  }
  return '—';
};

const getPrimaryEmail = (item: AccessRequestItem): string => {
  const primaryEmail = item.email ?? item.userEmail ?? item.user?.email;
  return primaryEmail ?? item.contactEmail ?? '';
};

const getInitialChar = (value: string): string => value.trim().charAt(0);

const getSecondInitial = (first: string, last: string): string => {
  const lastInitial = getInitialChar(last);
  if (lastInitial) {
    return lastInitial;
  }

  return first.trim().split(' ')[1]?.charAt(0) ?? '';
};

const getInitials = (item: AccessRequestItem): string => {
  const directFirstName = item.firstName ?? item.userFirstName ?? item.user?.firstName;
  const first = (directFirstName ?? item.name ?? '').trim();
  const last = (item.lastName ?? item.userLastName ?? item.user?.lastName ?? '').trim();
  const firstInitial = getInitialChar(first);
  const secondInitial = getSecondInitial(first, last);
  const initials = `${firstInitial}${secondInitial}`.toUpperCase();
  if (initials) {
    return initials;
  }
  return '—';
};

const getRequestedTimestamp = (item: AccessRequestItem): number => {
  const raw = item.requestedAt ?? item.createdAt;
  if (!raw) {
    return 0;
  }
  const parsed = Date.parse(raw);
  if (Number.isNaN(parsed)) {
    return 0;
  }
  return parsed;
};

const compareText = (a?: string | null, b?: string | null): number => {
  const sa = (a ?? '').toLowerCase();
  const sb = (b ?? '').toLowerCase();
  return sa.localeCompare(sb);
};

const getRequestDescription = (item: AccessRequestItem): string => {
  return item.reason ?? item.description ?? '';
};

const compareTimestamps = (a: number, b: number): number => {
  if (a < b) {
    return -1;
  }
  if (a > b) {
    return 1;
  }
  return 0;
};

const compareRequests = (a: AccessRequestItem, b: AccessRequestItem, sortColumn: SortColumn): number => {
  switch (sortColumn) {
    case 'email':
      return compareText(getPrimaryEmail(a), getPrimaryEmail(b));
    case 'description':
      return compareText(getRequestDescription(a), getRequestDescription(b));
    case 'created':
      return compareTimestamps(getRequestedTimestamp(a), getRequestedTimestamp(b));
    case 'name':
    default:
      return compareText(getDisplayName(a), getDisplayName(b));
  }
};

const getMessageFromErrorObject = (errorObject: { message?: unknown }): string | null => {
  const message = errorObject.message;

  if (typeof message === 'string') {
    return message;
  }

  if (typeof message === 'number' || typeof message === 'boolean' || typeof message === 'bigint') {
    return String(message);
  }

  if (message && typeof message === 'object') {
    try {
      return JSON.stringify(message);
    } catch {
      return 'Request failed';
    }
  }

  return null;
};

const getErrorMessage = (error: unknown): string => {
  if (typeof error === 'string') {
    return error;
  }

  if (error && typeof error === 'object' && 'message' in error) {
    return getMessageFromErrorObject(error) ?? 'Request failed';
  }

  return 'Request failed';
};

const getDeleteLabel = (item: AccessRequestItem | undefined): string => {
  if (!item) {
    return 'request';
  }

  const displayName = getDisplayName(item);
  if (displayName === '—') {
    return getPrimaryEmail(item) || 'request';
  }

  return displayName;
};

const getAriaSort = (sortColumn: SortColumn, activeColumn: SortColumn, sortOrder: SortOrder): 'ascending' | 'descending' | 'none' => {
  if (sortColumn !== activeColumn) {
    return 'none';
  }
  if (sortOrder === 'asc') {
    return 'ascending';
  }
  return 'descending';
};

const getSortIconOrder = (sortColumn: SortColumn, activeColumn: SortColumn, sortOrder: SortOrder): SortOrder | null => {
  if (sortColumn !== activeColumn) {
    return null;
  }
  return sortOrder;
};

interface EditUserModalContentProps {
  selectedUser: UserListItem | null;
  editFields: {
    role: string;
  };
  setEditFields: React.Dispatch<React.SetStateAction<{
    role: string;
  }>>;
  roles: string[];
  error?: string;
}

const EditUserModalContent: React.FC<EditUserModalContentProps> = ({
  selectedUser,
  editFields,
  setEditFields,
  roles,
  error
}) => {
  if (!selectedUser) {
    return null;
  }

  return (
    <div className="edit-user-modal">
      <div className="edit-user-modal__grid">
        <div className="edit-user-modal__field edit-user-modal__field--full">
          <label className="edit-user-modal__label" htmlFor="admin-edit-user-role">Role</label>
          <select
            id="admin-edit-user-role"
            className="edit-user-modal__select"
            value={editFields.role}
            onChange={(e) => setEditFields(prev => ({ ...prev, role: e.target.value }))}
          >
            <option value="">Select a role</option>
            {roles.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          {error && !editFields.role && <div className="edit-user-modal__error">{error}</div>}
        </div>
      </div>
    </div>
  );
};

const AccessRequestAdmin: React.FC = () => {
  const [requests, setRequests] = useState<AccessRequestItem[]>([]);
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});
  const [openModalConfirm, setOpenModalConfirm] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [globalRoles, setGlobalRoles] = useState<UserRole[]>([]);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState<UserListItem | null>(null);
  const [editFields, setEditFields] = useState({ role: "" });
  const [openModalChangeConfirm, setOpenModalChangeConfirm] = useState(false);
  const [modalError, setModalError] = useState<string>('');
  const confirmActionRef = useRef<(() => Promise<void>) | null>(null);

  const breadcrumbs = useBreadcrumbs({
      currentPageTitle: 'Admin - Access Request Management',
  }); 

  const roles = globalRoles.map(role => role.name);

  const loadRequests = async () => {
    setLoading(true);
    setError(null);
    try {
      const [response, usersRes, rolesRes] = await Promise.all([
        apiService.getAccessRequestData() as Promise<{ success: boolean; data: AccessRequestItem[] }>,
        listUsers(),
        getRoles(),
      ]);
      if (response?.success && response.data) {
        setRequests(response.data);
        // select first item by default when loading
        const firstId = response.data?.[0]?.id;
        const normalizedFirstId = firstId == null ? null : String(firstId);
        setSelectedId(normalizedFirstId);
      } else {
        setError('Failed to load access requests');
      }
      if (usersRes?.success && usersRes.data) {
        setUsers(usersRes.data);
      }
      if (rolesRes?.success && rolesRes.data) {
        setGlobalRoles(rolesRes.data);
      }
    } catch (e: unknown) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  const [sortColumn, setSortColumn] = useState<SortColumn>('created');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<{ text: string; kind?: StatusKind } | null>(null);


  const getRequestStatus = (item: AccessRequestItem): { label: string; modifier: string } => {
    if (item.status) {
      const normalised = item.status.toLowerCase();
      if (normalised === 'approved' || normalised === 'active') {
        return { label: item.status, modifier: 'approved' };
      }
      return { label: item.status, modifier: 'pending' };
    }
    const reqEmail = getPrimaryEmail(item).toLowerCase();
    if (reqEmail) {
      const matchedUser = users.find(u => (u.email ?? '').toLowerCase() === reqEmail);
      // Only consider approved if the user's record was updated *after* the request was submitted,
      // indicating the role was patched in response to this request.
      const requestedTime = getRequestedTimestamp(item);
      const updatedTime = matchedUser?.updatedAt ? Date.parse(matchedUser.updatedAt) : 0;
      if (matchedUser?.role && requestedTime > 0 && updatedTime > requestedTime) {
        return { label: 'Approved', modifier: 'approved' };
      }
    }
    return { label: 'Pending', modifier: 'pending' };
  };

  const sortedRequests = useMemo(() => {
    if (!requests || requests.length === 0) { 
      return requests;
    }
    const copy = [...requests];
    const multiplier = sortOrder === 'asc' ? 1 : -1;

    copy.sort((a, b) => compareRequests(a, b, sortColumn) * multiplier);

    return copy;
  }, [requests, sortColumn, sortOrder]);

  const visibleRequests = sortedRequests ?? requests;

  useEffect(() => {
    if (selectedId) {
      return;
    }

    const firstId = visibleRequests?.[0]?.id;
    if (firstId == null) {
      return;
    }

    setSelectedId(String(firstId));
  }, [visibleRequests, selectedId]);

  const handleSort = (column: SortColumn, defaultOrder: SortOrder) => {
    setSortColumn(column);
    setSortOrder(prev => {
      if (sortColumn === column) {
        if (prev === 'asc') {
          return 'desc';
        }
        return 'asc';
      }
      return defaultOrder;
    });
  };

  const confirmAndDelete = (id: string) => {
    setConfirmDeleteId(id);
    setOpenModalConfirm(true);
  };

  const handleDeleteRequest = async (id: string) => {
    setActionLoading(prev => ({ ...prev, [id]: true }));
    try {
      // capture name for status before we reload
      const toDelete = requests.find(r => String(r.id) === String(id));
      const displayName = getDeleteLabel(toDelete);
      const response = await deleteAccessRequest(id);
      if (response?.success) {
        setOpenModalConfirm(false);
        setConfirmDeleteId(null);
        await loadRequests();
        setStatusMessage({ text: `Successfully deleted ${displayName} access request`, kind: 'success' });
      } else {
        const errorMsg = response?.error ? getErrorMessage(response.error) : 'Failed to delete access request';
        setStatusMessage({ text: errorMsg, kind: 'error' });
      }
        window.scrollTo(0, 0);
    } catch (e: unknown) {
      setStatusMessage({ text: getErrorMessage(e), kind: 'error' });
    } finally {
      setActionLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleApproveRequest = (requestItem: AccessRequestItem) => {
    const reqEmail = getPrimaryEmail(requestItem).toLowerCase();
    const matchedUser = users.find(u => (u.email ?? '').toLowerCase() === reqEmail);
    
    if (!matchedUser) {
      setStatusMessage({ text: 'User not found in the system', kind: 'error' });
      return;
    }

    setSelectedUserForEdit(matchedUser);
    setEditFields({ role: '' });
    setOpenEditModal(true);
  };

  const handleEditConfirm = () => {
    if (!selectedUserForEdit) {
      return;
    }

    if (!editFields.role) {
      setModalError('Please select a role');
      return;
    }

    setModalError('');

    confirmActionRef.current = async () => {
      setOpenModalChangeConfirm(false);
      await updateUserData(editFields);
    };

    setOpenModalChangeConfirm(true);
  };

  const updateUserData = async (localData: { role: string; roleId?: number }) => {
    if (!selectedUserForEdit) {
      return;
    }

    setActionLoading(prev => ({ ...prev, [selectedUserForEdit.id]: true }));
    
    const roleIdValue = globalRoles.find(r => r.name === localData.role)?.id;
    const roleId = typeof roleIdValue === 'number' ? roleIdValue : Number(roleIdValue);
    
    try {
        const roleUpdate = await updateUserRole(selectedUserForEdit.id, roleId);
        if (roleUpdate?.success) {
          setOpenEditModal(false);
          await loadRequests();
          setStatusMessage({ text: 'Access request approved and user role updated successfully', kind: 'success' });
        } else {
          setStatusMessage({ text: roleUpdate?.error?.message ?? 'Failed to update role', kind: 'error' });
        }
      window.scrollTo(0, 0);
    } catch (e: unknown) {
      setStatusMessage({ text: getErrorMessage(e), kind: 'error' });
    } finally {
      setActionLoading(prev => ({ ...prev, [selectedUserForEdit.id]: false }));
    }
  };

  if (loading) {
    return (
      <main>
        <div className="container">
          <section className='page'>
            <h2 className="heading__title heading__title--building">Access Request Management</h2>
            <DateTimeDisplay />
            <Breadcrumbs items={breadcrumbs} />
          </section>
          <section className="page-section">
            <LoadingSpinner message="Loading requests…" />
          </section>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main>
        <div className="container">
          <section className='page'>
            <h2 className="heading__title heading__title--building">Access Request Management</h2>
            <DateTimeDisplay />
            <Breadcrumbs items={breadcrumbs} />
          </section>
          <section className="page-section">
            <div>Error loading requests: {error}</div>
            <button className="btn btn--secondary" onClick={loadRequests}>
              Retry
            </button>
          </section>
        </div>
      </main>
    );
  }

  return (
    <main>
      <div className="container">
            <Modal
              isOpen={openModalConfirm}
              title="Delete Request"
              headerClass="modal__header--danger"
              content={<ModalContent  
                heading="Delete Request"
                message="Are you sure you want to delete this access request?"/>}
              onConfirm={async () => {
                if (confirmDeleteId) {
                  await handleDeleteRequest(confirmDeleteId);
                }
              }}
              onCancel={() => { setOpenModalConfirm(false); setConfirmDeleteId(null); }}
            />

            <Modal
              isOpen={openEditModal}
              title="Update User Role"
              content={
                <EditUserModalContent
                  selectedUser={selectedUserForEdit}
                  editFields={editFields}
                  setEditFields={setEditFields}
                  roles={roles}
                  error={modalError}
                />
              }
              onConfirm={handleEditConfirm}
              onCancel={() => {
                setOpenEditModal(false);
                setModalError('');
              }}
            />

            <Modal
              isOpen={openModalChangeConfirm}
              title="Confirm Changes"
              content={<ModalContent message="Are you sure you want to apply these changes?" />}
              onConfirm={async () => {
                if (confirmActionRef.current) {
                  await confirmActionRef.current();
                  setOpenModalChangeConfirm(false);
                  setOpenEditModal(false);
                }
              }}
              onCancel={() => setOpenModalChangeConfirm(false)}
            />

        <section className='page'>
          <h2 className="heading__title heading__title--admin">Access Request Management</h2>
          <DateTimeDisplay />
          <Breadcrumbs items={breadcrumbs} />
        </section>

{statusMessage && (
          <StatusMessage kind={statusMessage.kind || 'success'} onClose={() => setStatusMessage(null)}>
            {statusMessage.text}
          </StatusMessage>
        )}

        <section className="alarm-content mb-2 p-1">
        
          <h3 className="heading__title heading__title--users mb-1 mt-0">
            Access Requests ({requests.length})
          </h3>

          {requests.length > 0 ? (
            <div className='admin-requests'>
              <div className='admin-requests__table'>
                <table className="table-responsive">
                  <thead>
                    <tr>
                      <th onClick={() => { handleSort('name', 'asc'); }} aria-sort={getAriaSort(sortColumn, 'name', sortOrder)} className="th--sortable">
                        Name <SortIcon sortOrder={getSortIconOrder(sortColumn, 'name', sortOrder)} />
                      </th>
                      <th onClick={() => { handleSort('email', 'asc'); }} aria-sort={getAriaSort(sortColumn, 'email', sortOrder)} className="th--sortable">
                        Email <SortIcon sortOrder={getSortIconOrder(sortColumn, 'email', sortOrder)} />
                      </th>
                      <th onClick={() => { handleSort('created', 'desc'); }} aria-sort={getAriaSort(sortColumn, 'created', sortOrder)} className="th--sortable th--right">
                        Requested On <SortIcon sortOrder={getSortIconOrder(sortColumn, 'created', sortOrder)} />
                      </th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRequests.map(req => {
                      const reqId = req.id == null ? null : String(req.id);
                      const isSelected = selectedId === reqId;
                      return (
                        <tr
                          key={req.id}
                          onClick={() => {
                            if (reqId) {
                              setSelectedId(reqId);
                            }
                          }}
                          className={`admin-request-row${isSelected ? ' admin-request-row--selected' : ''}`}
                        >
                          <td>
                            <div className='admin-requests--row-content'>
                              <figure className='m-0'>
                                <div className='initials-badge'>
                                  {getInitials(req)}
                                </div>
                              </figure>
                              <span>{getDisplayName(req)}</span>
                            </div>
                          </td>
                          <td>
                            {getPrimaryEmail(req) ? <span className="alarm-name-link">{getPrimaryEmail(req)}</span> : '—'}
                          </td>
                          <td style={{ textAlign: 'right' }}>{(formatDateTime(req.requestedAt ?? req.createdAt) || '—')}</td>
                          <td>
                            {(() => { const s = getRequestStatus(req); return <span className={`status status__${s.modifier}`}>{s.label}</span>; })()}
                          </td>
                          <td>
                            {(() => {
                              const status = getRequestStatus(req);
                              const isPending = status.modifier === 'pending';
                              const actions = [];
                              
                              if (isPending) {
                                actions.push({
                                  label: 'Approve',
                                  onClick: (e?: React.MouseEvent<HTMLElement>) => { e?.stopPropagation(); handleApproveRequest(req); },
                                  disabled: reqId ? actionLoading[reqId] : false,
                                  className: 'actions__link--approve',
                                  itemClassName: 'actions__item--approve'
                                });
                              }
                              
                              actions.push({
                                label: 'Delete',
                                onClick: (e?: React.MouseEvent<HTMLElement>) => { e?.stopPropagation(); if (reqId) { confirmAndDelete(reqId); } },
                                disabled: reqId ? actionLoading[reqId] : false,
                                className: 'actions__link--delete',
                                itemClassName: 'actions__item--delete'
                              });
                              
                              return (
                                <ActionList
                                  customActions={actions}
                                  disabled={reqId ? actionLoading[reqId] : false}
                                />
                              );
                            })()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className='admin-requests__panel'>
                {selectedId ? (
                  (() => {
                    const sel = visibleRequests.find(r => String(r.id) === selectedId) || null;
                    const selId = sel?.id == null ? null : String(sel.id);
                    if (!sel) {
                      return <div>Select a request to view details.</div>;
                    }
                    return (
                      <div>
                        <div className='admin-requests--panel-heading'>
                          <figure>
                            <div className='initials-badge'>
                              {getInitials(sel)}
                            </div>
                          </figure>
                          <h4>{getDisplayName(sel)}</h4>
                        </div>
                        <div className='admin-requests--panel-email'>{getPrimaryEmail(sel) || '—'}</div>
                        {(() => {
                          const empId = sel.employeeId ?? sel.employeeID
                            ?? users.find(u => (u.email ?? '').toLowerCase() === getPrimaryEmail(sel).toLowerCase())?.employeeId;
                          return empId
                            ? <div className='admin-requests--panel-employee'><strong>Employee ID:</strong> {empId}</div>
                            : null;
                        })()}
                        <div className='admin-requests--panel-date'><strong>Requested On:</strong> {formatDateTime(sel.requestedAt ?? sel.createdAt) || '—'}</div>
                        <div className='admin-requests--panel-status'>
                          <strong>Status:</strong>{' '}
                          {(() => { const s = getRequestStatus(sel); return <span className={`status status__${s.modifier}`}>{s.label}</span>; })()}
                        </div>
                        <div>
                          <h5>Reason:</h5>
                          <div className='admin-requests__panel--reason'>{sel.reason ?? sel.description ?? sel.requestReason ?? '—'}</div>
                        </div>
                        <div className='admin-requests__buttons'>
                          {(() => {
                            const status = getRequestStatus(sel);
                            const isPending = status.modifier === 'pending';
                            return (
                              <>
                                {isPending && (
                                  <button className="btn btn__primary" onClick={() => { handleApproveRequest(sel); }} disabled={selId ? actionLoading[selId] : false}>Approve Request</button>
                                )}
                                <button className="btn btn__error" onClick={() => { if (selId) { confirmAndDelete(selId); } }} disabled={selId ? actionLoading[selId] : false}>Delete Request</button>
                                <a
                                  className="btn btn__dark--outline"
                                  href={`mailto:${getPrimaryEmail(sel)}?subject=SmartAlarm%20Request%20response`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ textDecoration: 'none', display: 'inline-block' }}
                                >
                                  Reply
                                </a>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  <div>Select a request to view details.</div>
                )}
              </div>
            </div>
          ) : (
            <div className="empty-state">
              <p>No access requests found.</p>
            </div>
          )}
        </section>
      </div>
    </main>
  );
};

export default AccessRequestAdmin;


